/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Typography } from '@material-ui/core'
import React, { useEffect } from 'react'
import { connect, useDispatch } from 'react-redux'
import styled from 'styled-components'
import ldRedux from 'ld-redux'
import { isEqual } from 'lodash'
import { parse, getTime } from 'date-fns'

import { loadProfileStart } from './redux/modules/profile'
import { selectUserLocationId } from './redux/modules/selectors'
import { currentUserStart } from './redux/modules/user'
import { loadLocationStart } from './redux/modules/location'
import { loadTeamStart } from './redux/modules/team'
import { loadUserTeamStart } from './redux/modules/userTeam'
import { selectUserTeam } from './redux/selectors/userTeamSelectors'

//this component loads globally necessary data objects (and objects that need to be known on load, for various reasons)
//Specifically, Launch Darkly uses a lot of this data to identify the user, and generally provides a more fluid loading experience
const GlobalDataLoader = ({
  children,
  currentUser = {},
  location = {},
  profile = {},
  currentUserStart,
  loadProfileStart,
  loadLocationStart,
  isAuthenticated,
  userInitialized,
  profileInitialized,
  locationInitialized,
  userTeam,
  team,
  userTeamInitialized,
  teamInitialized,
  loadUserTeamStart,
  loadTeamStart,
  isLDReady,
}) => {
  const dispatch = useDispatch()

  useEffect(() => {
    currentUserStart()
  }, [currentUserStart])

  //load more stuff any time current user is updated
  useEffect(() => {
    if (currentUser.userId && userInitialized) {
      loadProfileStart()
      loadUserTeamStart()
    }
  }, [currentUser?.userId, userInitialized, loadProfileStart, loadUserTeamStart])

  useEffect(() => {
    if (currentUser?.deliveryLocationId && userInitialized) {
      loadLocationStart(currentUser.deliveryLocationId)
    }
  }, [currentUser?.deliveryLocationId, userInitialized, loadLocationStart])

  useEffect(() => {
    // TODO We won't need to load `team` in the global loader when/if we get the necessary information in the aggregated (`userTeam`) response
    if (userTeam?.team?.manager) {
      loadTeamStart(userTeam?.team?.accountId)
    }
  }, [loadTeamStart, userTeam])

  useEffect(() => {
    if (userInitialized) {
      let payload = {
        clientSideId: process.env.REACT_APP_LD_KEY,
        dispatch: dispatch,
        useCamelCaseFlagKeys: true,
      }

      if (
        isAuthenticated &&
        userInitialized &&
        profileInitialized &&
        locationInitialized &&
        teamInitialized
      ) {
        payload.user = {
          key: profile?.userId.toString(), //launch darkly requires a key property for a user, which will be their userId
          email: currentUser?.email,
          custom: {
            userCreatedDate: getTime(parse(profile?.createdDate)),
            locationId: location?.deliveryLocationId,
            locationActivationDate: getTime(parse(location?.activationDate)),
            locationActive: location?.isActive,
            marketId: location?.marketId,
            teamId: userTeam?.team?.accountId,
            teamAdmin: userTeam?.team?.manager,
            teamCreatedDate: team?.createdDate && getTime(parse(team.createdDate)), // TODO This won't be available for non-admin users
            teamPlanName: team?.plan?.displayName, // TODO This won't be available for non-admin users
          },
        }
        //only run this if no client is set or if the updated payload is different than what's already set
        if (
          !window.ldClient ||
          (window.ldClient && !isEqual(payload.user, window.ldClient.getUser()))
        ) {
          ldRedux.init(payload)
        }
      }
    }
  }, [
    userInitialized,
    profileInitialized,
    locationInitialized,
    teamInitialized,
    currentUser?.email,
    currentUser?.userId,
    profile?.locationId,
    profile?.createdDate,
    location?.deliveryLocationId,
    location?.marketId,
    location?.isActive,
    userTeam?.team?.accountId,
    userTeam?.team?.manager,
    team?.createdDate,
    team?.plan?.displayName,
  ])

  if (
    isAuthenticated &&
    (!userInitialized ||
      !profileInitialized ||
      !locationInitialized ||
      !userTeamInitialized ||
      !teamInitialized ||
      !isLDReady)
  ) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        width="100%"
      >
        <Box maxWidth="200px" clone>
          <img alt="" src={`${process.env.REACT_APP_BASE_ORDER_URL}/images/logo-foodsby.png`} />
        </Box>
        <Box textAlign="left" width="55px" mt="25px">
          <Loader variant="subtitle2">
            Loading<span>.</span>
            <span>.</span>
            <span>.</span>
          </Loader>
        </Box>
      </Box>
    )
  } else {
    return <>{children}</>
  }
}

const mapStateToProps = state => {
  const locationId = selectUserLocationId(state)
  const { isAuthenticated, isCurrentUserLoading, currentUser, userInitialized } = state.user
  const { profileInitialized, isLoadingProfile, profile } = state.profile
  const { locationInitialized, isLocationLoading, location } = state.location
  const { team, teamInitialized } = state.team
  const userTeam = selectUserTeam(state)
  const { userTeamInitialized } = state.userTeam
  const { isLDReady } = state.featureFlags
  return {
    isAuthenticated,
    isCurrentUserLoading,
    isLoadingProfile,
    locationId,
    profile,
    currentUser,
    isLocationLoading,
    location,
    userInitialized,
    profileInitialized,
    locationInitialized,
    team,
    userTeam,
    teamInitialized,
    userTeamInitialized,
    isLDReady,
  }
}
const mapDispatchToProps = {
  currentUserStart,
  loadProfileStart,
  loadLocationStart,
  loadUserTeamStart,
  loadTeamStart,
}
export default connect(mapStateToProps, mapDispatchToProps)(GlobalDataLoader)

const Loader = styled(Typography)`
  & span {
    font-size: 25px;
    animation-name: blink;
    animation-duration: 1.4s;
    animation-iteration-count: infinite;
    animation-fill-mode: both;
  }

  & span:nth-child(2) {
    animation-delay: 0.2s;
  }

  & span:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes blink {
    0% {
      opacity: 0.2;
    }
    20% {
      opacity: 1;
    }
    100% {
      opacity: 0.2;
    }
  }
`
